/** A masked guest link with its audited Reveal (plan §4.4 `booking.link.revealed`): the
 *  booking-management link logs whoever holds it in as the guest, the review page lets
 *  them review as the guest — so the record shows only the masked form, and revealing it
 *  is a confirmed, logged action (`console_session_reveal`; without it there is no
 *  Reveal at all). The revealed link stays on this screen only — copy it, or hide it
 *  again; it is never stored. */
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useRef, useState } from 'react';

import { revealBookingLink, type BookingLinkKind } from '@/api/bookingActions';
import { usePermissions } from '@/hooks/usePermissions';

import { CopyIconButton } from '../../overview/common';
import { ImmediateActionPopover } from '../ImmediateActionPopover';

import { MONO_SX } from './displays';

export const REVEAL_PERM = 'console_session_reveal';

const WHAT: Record<BookingLinkKind, { title: string; holder: string; copy: string }> = {
  bookingmgmt: {
    title: 'Reveal the booking-management link',
    holder: 'logs whoever holds it in as the guest',
    copy: 'Booking-management link',
  },
  review: {
    title: 'Reveal the review link',
    holder: 'lets whoever holds it review the trip as the guest',
    copy: 'Review link',
  },
};

export function MaskedLinkReveal({
  bookingId,
  kind,
  masked,
}: {
  bookingId: number;
  kind: BookingLinkKind;
  masked: string;
}) {
  const { hasPerm } = usePermissions();
  const [url, setUrl] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const what = WHAT[kind];

  return (
    <Box
      component="span"
      data-masked-link={kind}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}
    >
      <Box component="span" sx={{ ...MONO_SX, overflowWrap: 'anywhere' }} data-revealed={url ? true : undefined}>
        {url ?? masked}
      </Box>
      {url ? (
        <>
          <CopyIconButton value={url} label={what.copy} />
          <Button
            size="small"
            startIcon={<VisibilityOffOutlinedIcon />}
            sx={{ py: 0 }}
            onClick={() => {
              setUrl(null);
              requestAnimationFrame(() => buttonRef.current?.focus());
            }}
          >
            Hide
          </Button>
        </>
      ) : (
        hasPerm(REVEAL_PERM) && (
          <Button
            ref={buttonRef}
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            sx={{ py: 0 }}
            aria-haspopup="dialog"
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            Reveal
          </Button>
        )
      )}
      {anchor && (
        <ImmediateActionPopover
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          title={what.title}
          confirmLabel="Reveal"
          run={() => revealBookingLink(bookingId, kind)}
          onDone={(r) => {
            setAnchor(null);
            setUrl(r.url);
          }}
          dataKey={`reveal_${kind}`}
        >
          <Typography variant="body2" component="p" sx={{ m: 0 }}>
            <strong>This is logged.</strong> The link {what.holder} — share it only with the guest.
          </Typography>
        </ImmediateActionPopover>
      )}
    </Box>
  );
}
